// packages/web/src/components/account/auth-modal.tsx
/**
 * AuthModal — the account-system entry point.
 *
 * Single modal container with an internal mode state machine:
 *   login ⇄ signup ⇄ magic(input) → magicSent
 *   password (change-password, opened from account drawer)
 *
 * Anonymous users see it via UserAvatar click; "建课/生成/充值" CTAs open it
 * when isAnonymous. Esc closes; overlay click closes.
 *
 * Visual: spec 附录 A · 印章学术 · see styles/pages/account.css.
 * Spec: docs/architecture/account-system-design.md §1.1, §9.1.2
 */

import { useEffect, useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { AuthLoginForm } from './auth-login-form.js';
import { AuthSignupForm } from './auth-signup-form.js';
import { AuthSignupSentView } from './auth-signup-sent-view.js';
import { AuthSignupVerify } from './auth-signup-verify.js';
import { AuthMagicLink } from './auth-magic-link.js';
import { AuthMagicVerify } from './auth-magic-verify.js';
import { AuthChangePasswordForm } from './auth-change-password.js';
import { AuthForgotForm } from './auth-forgot-form.js';
import { AuthResetForm } from './auth-reset-form.js';

/**
 * AuthModal modes.
 *
 * 19BN Phase D D3 added the password-reset flow:
 *   - 'forgot'        — enter email, request a reset_password token
 *   - 'forgotSent'    — "check your email" confirmation (mirrors magicSent)
 *   - 'resetPassword' — set a new password using the token from the email link
 *
 * 19BO+ Phase B added the two-stage signup flow:
 *   - 'signupSent'    — "verification email sent" confirmation after the
 *                       request stage (mirrors magicSent / forgotSent)
 *   - 'signupVerify'  — opened by App root when the URL carries ?signup-token=
 *                       on load; calls /auth/signup/verify to complete stage 2
 *
 * 19BO+ CROSS-ORIGIN-AUTH added SPA-consumed magic-link verify:
 *   - 'magicVerify'   — opened by App root when the URL carries ?magic-token=
 *                       on load; calls /auth/magic/verify to complete the login
 *
 * The resetPassword / signupVerify / magicVerify modes are opened by App root
 * when the URL carries the corresponding token param. They are not reachable
 * from inside the modal via a normal switch — the tokens only arrive via
 * email links.
 */
export type AuthMode =
  | 'login'
  | 'signup'
  | 'signupSent'
  | 'signupVerify'
  | 'magic'
  | 'magicSent'
  | 'magicVerify'
  | 'password'
  | 'forgot'
  | 'forgotSent'
  | 'resetPassword';

export interface AuthModalProps {
  open: boolean;
  initialMode?: AuthMode;
  /**
   * Reset-password token (only used when initialMode='resetPassword'). The App
   * root reads it from the ?reset-token= URL param and passes it down; the
   * reset form never asks the user to type it.
   */
  resetToken?: string;
  /**
   * 19BO+ Phase B: signup-verify token (only used when initialMode='signupVerify').
   * The App root reads it from the ?signup-token= URL param and passes it down.
   */
  signupToken?: string;
  /**
   * 19BO+ CROSS-ORIGIN-AUTH: magic-link verify token (only used when
   * initialMode='magicVerify'). The App root reads it from the ?magic-token=
   * URL param and passes it down.
   */
  magicToken?: string;
  onClose: () => void;
}

export function AuthModal({ open, initialMode = 'login', resetToken, signupToken, magicToken, onClose }: AuthModalProps) {
  const t = useT();
  const [mode, setMode] = useState<AuthMode>(initialMode);

  // Reset to initialMode each time the modal opens.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset mode to initialMode each time the modal opens; key-reset alternative needs parent to own the open prop
    if (open) setMode(initialMode);
  }, [initialMode, open]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="auth-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div className="auth-dialog" role="dialog" aria-modal="true" aria-label={t('auth.createAccount')}>
        {mode === 'login' && <AuthLoginForm onSwitchMode={setMode} onClose={onClose} />}
        {mode === 'signup' && <AuthSignupForm onSwitchMode={setMode} onClose={onClose} />}
        {mode === 'signupSent' && <AuthSignupSentView onSwitchMode={setMode} />}
        {mode === 'signupVerify' && signupToken && (
          <AuthSignupVerify token={signupToken} onSwitchMode={setMode} onClose={onClose} />
        )}
        {mode === 'magic' && <AuthMagicLink step="input" onSwitchMode={setMode} onClose={onClose} />}
        {mode === 'magicSent' && <AuthMagicLink step="sent" onSwitchMode={setMode} onClose={onClose} />}
        {mode === 'magicVerify' && magicToken && (
          <AuthMagicVerify token={magicToken} onSwitchMode={setMode} onClose={onClose} />
        )}
        {mode === 'password' && <AuthChangePasswordForm onClose={onClose} />}
        {mode === 'forgot' && <AuthForgotForm onSwitchMode={setMode} />}
        {mode === 'forgotSent' && (
          <div className="auth-body" style={{ textAlign: 'center', padding: 'var(--space-4xl) var(--space-3xl)' }}>
            <div className="auth-magic-sent-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-success-deep)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-ink)', margin: 'var(--space-xl) 0 0' }}>
              重置链接已发送
            </h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', margin: 'var(--space-md) 0 0' }}>
              若该邮箱已注册，你将收到一封包含密码重置链接的邮件。
            </p>
            <div className="auth-switch" style={{ marginTop: 'var(--space-2xl)' }}>
              <button type="button" onClick={() => setMode('login')}>← {t('auth.welcome')}</button>
            </div>
          </div>
        )}
        {mode === 'resetPassword' && resetToken && (
          <AuthResetForm token={resetToken} onSwitchMode={setMode} />
        )}
      </div>
    </div>
  );
}
